/**
 * Settings → Async Jobs: dispatcher tuning.
 *
 * Monitoring → Async Jobs stays what it is (the live queue and its timings);
 * this is the knobs. Values live in the `async-job-config` settings doc and are
 * read through a 60s-cached resolver, so a change is live without a deploy.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, InfoTip, IntegerInput } from '@shared-ui/components';
import { adminApi } from '../../api/adminApi';
import type { AsyncJobTypeConfig } from '../../types';
import './AsyncJobSettings.css';

/** What this page can change. The rest of AsyncJobConfig stays compiled-in —
 *  add a field here when there is a reason to tune it from the console. */
type EditableField = 'concurrencyCap' | 'maxAttempts' | 'timeoutMs';
const EDITABLE: EditableField[] = ['concurrencyCap', 'maxAttempts', 'timeoutMs'];

type Draft = Record<string, Partial<Record<EditableField, number>>>;

/** Current value, with the compiled default in brackets when it has been changed. */
function ValueCell({ value, fallback, suffix = '' }: { value: number; fallback: number; suffix?: string }) {
    const overridden = value !== fallback;
    return (
        <span className={overridden ? 'ajs-value ajs-value--overridden' : 'ajs-value'}>
            {value}{suffix}
            {overridden && <span className="ajs-default"> ({fallback}{suffix})</span>}
        </span>
    );
}

export function AsyncJobSettings() {
    const queryClient = useQueryClient();
    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['async-job-config'],
        queryFn: () => adminApi.getAsyncJobConfig(),
    });

    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<Draft>({});
    const [status, setStatus] = useState<string | null>(null);

    // Clear the draft when fresh server state lands, so a save or refetch can't
    // leave stale edits sitting over new values.
    useEffect(() => { setDraft({}); }, [data]);

    const valueOf = (t: AsyncJobTypeConfig, f: EditableField): number =>
        draft[t.type]?.[f] ?? t.effective[f];

    const dirtyCount = useMemo(() => {
        if (!data?.types) return 0;
        return data.types.filter(t => EDITABLE.some(f => valueOf(t, f) !== t.effective[f])).length;
    }, [data, draft]);

    const save = useMutation({
        mutationFn: () => {
            // Send every value that differs from its HANDLER DEFAULT — that is what
            // an override is. A field back at its default is omitted, which clears it.
            const overrides: Draft = {};
            for (const t of data?.types ?? []) {
                const next: Partial<Record<EditableField, number>> = {};
                for (const f of EDITABLE) {
                    const v = valueOf(t, f);
                    if (v !== t.handlerDefault[f]) next[f] = v;
                }
                if (Object.keys(next).length > 0) overrides[t.type] = next;
            }
            return adminApi.saveAsyncJobConfig(overrides);
        },
        onSuccess: () => {
            setStatus('Saved — live within 60s.');
            setEditing(false);
            queryClient.invalidateQueries({ queryKey: ['async-job-config'] });
        },
        onError: (e: Error) => setStatus(`Failed: ${e.message}`),
    });

    const setField = (type: string, field: EditableField, value: number) => {
        setDraft(prev => ({ ...prev, [type]: { ...prev[type], [field]: value } }));
        setStatus(null);
    };

    if (isLoading) return <p className="muted">Loading job config…</p>;
    if (isError) return <p className="error-message">Failed to load: {(error as Error).message}</p>;

    const rows = data?.types ?? [];

    return (
        <div className="settings-section">
            <div className="section-header">
                <h2>Dispatcher</h2>
                <p className="section-description">
                    Tick interval <code>{data?.dispatcher.schedule ?? '—'}</code> — read-only here, and
                    only a fallback: enqueueing a job signals the dispatcher directly.
                </p>
            </div>

            <div className="section-header">
                <h2>Per-type limits</h2>
                <p className="section-description">
                    Raising a cap only helps if jobs are arriving to a full one — check pickup times
                    under Monitoring → Async Jobs first.
                </p>
            </div>

            <table className="dt dt--compact ajs-table">
                <thead>
                    <tr>
                        <th>Type</th>
                        <th style={{ width: 180 }}>Concurrency cap</th>
                        <th style={{ width: 180 }}>
                            Attempts
                            <InfoTip text="Total attempts including the first — 3 means one run plus two retries. Only transient failures are retried." />
                        </th>
                        <th style={{ width: 180 }}>Timeout (s)</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((t: AsyncJobTypeConfig) => (
                        <tr key={t.type}>
                            <td><code>{t.type}</code></td>
                            <td>
                                {editing ? (
                                    <IntegerInput
                                        min={1} max={100}
                                        className="ui-input ajs-input"
                                        value={valueOf(t, 'concurrencyCap')}
                                        onChange={v => { if (v != null) setField(t.type, 'concurrencyCap', v); }}
                                    />
                                ) : (
                                    <ValueCell value={t.effective.concurrencyCap} fallback={t.handlerDefault.concurrencyCap} />
                                )}
                            </td>
                            <td>
                                {editing ? (
                                    <IntegerInput
                                        min={1} max={10}
                                        className="ui-input ajs-input"
                                        value={valueOf(t, 'maxAttempts')}
                                        onChange={v => { if (v != null) setField(t.type, 'maxAttempts', v); }}
                                    />
                                ) : (
                                    <ValueCell value={t.effective.maxAttempts} fallback={t.handlerDefault.maxAttempts} />
                                )}
                            </td>
                            <td>
                                {editing ? (
                                    <IntegerInput
                                        min={1} max={1800}
                                        className="ui-input ajs-input"
                                        // Stored in ms, edited in seconds — nobody wants to type 180000.
                                        value={Math.round(valueOf(t, 'timeoutMs') / 1000)}
                                        onChange={v => { if (v != null) setField(t.type, 'timeoutMs', v * 1000); }}
                                    />
                                ) : (
                                    <ValueCell
                                        value={Math.round(t.effective.timeoutMs / 1000)}
                                        fallback={Math.round(t.handlerDefault.timeoutMs / 1000)}
                                        suffix="s"
                                    />
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="ajs-actions">
                {editing ? (
                    <>
                        <Button
                            variant="primary"
                            disabled={dirtyCount === 0 || save.isPending}
                            onClick={() => save.mutate()}
                        >
                            {save.isPending ? 'Saving…' : `Save${dirtyCount ? ` (${dirtyCount})` : ''}`}
                        </Button>
                        <Button variant="ghost" onClick={() => { setDraft({}); setEditing(false); setStatus(null); }}>
                            Cancel
                        </Button>
                    </>
                ) : (
                    <Button variant="secondary" onClick={() => setEditing(true)}>Edit</Button>
                )}
                {status && <span className="muted">{status}</span>}
            </div>
        </div>
    );
}

export default AsyncJobSettings;
